import { z } from 'zod';

const MissingFoodSchema = z.object({
  name: z.string(),
  source_qty: z.unknown().nullable().optional(),
  source_uom: z.unknown().nullable().optional(),
  stock_qty: z.unknown().nullable().optional(),
  stock_uom: z.unknown().nullable().optional(),
  proposed_uom: z.unknown().nullable().optional(),
  conversion_factor: z.unknown().nullable().optional(),
  conversion_source: z.unknown().nullable().optional(),
});

const ReviewLineSchema = z.object({
  name: z.string(),
  food_id: z.string().uuid().nullable().optional(),
  is_new: z.boolean(),
  source_qty: z.unknown().nullable().optional(),
  source_uom: z.unknown().nullable().optional(),
  stock_qty: z.unknown().nullable().optional(),
  stock_uom: z.unknown().nullable().optional(),
  proposed_uom: z.unknown().nullable().optional(),
  conversion_factor: z.unknown().nullable().optional(),
  conversion_source: z.unknown().nullable().optional(),
  batch_lot: z.unknown().nullable().optional(),
  expiration_date: z.unknown().nullable().optional(),
});

export const DocumentImportReviewSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  business_id: z.string().uuid(),
  doc_kind: z.enum(['ddt', 'fattura']),
  status: z.enum(['awaiting_review', 'approved', 'rejected']),
  submitted_by_user_id: z.string().uuid(),
  reviewed_by_user_id: z.string().uuid().nullable(),
  committed_document_id: z.string().uuid().nullable(),
  rejection_reason: z.string().nullable(),
  summary: z.object({
    document_number: z.string().nullable().optional(),
    document_date: z.string().nullable().optional(),
    supplier_name: z.string().nullable().optional(),
    document_total: z.unknown().nullable().optional(),
    currency: z.string().nullable().optional(),
    lines_count: z.number().int().nonnegative().optional(),
    document_job_id: z.string().uuid().nullable().optional(),
    missing_foods: z.array(MissingFoodSchema).default([]),
    lines: z.array(ReviewLineSchema).default([]),
    conformity: z.unknown().nullable().optional(),
  }),
  created_at: z.string(),
  updated_at: z.string(),
});

export type DocumentImportReview = z.infer<typeof DocumentImportReviewSchema>;

const ReviewPlanLinePayloadSchema = z.object({
  parsed_index: z.number().int().nonnegative().optional(),
  raw_description: z.string().nullable().optional(),
  supplier_code: z.string().nullable().optional(),
  food_id: z.string().uuid().nullable().optional(),
  food_create: z.record(z.unknown()).nullable().optional(),
  qty: z.union([z.number(), z.string()]).nullable().optional(),
  uom: z.string().nullable().optional(),
  source_qty: z.number().nullable().optional(),
  source_uom: z.string().nullable().optional(),
  source_unit_price: z.number().nullable().optional(),
  source_line_total: z.number().nullable().optional(),
  currency: z.string().nullable().optional(),
  batch_lot: z.string().nullable().optional(),
  expiration_date: z.string().nullable().optional(),
  is_frozen: z.boolean().optional(),
  notify_expiration: z.boolean().optional(),
  storage_unit_id: z.string().nullable().optional(),
  supplier_sku: z.string().nullable().optional(),
  keep_current_lot: z.boolean().optional(),
  replace_active_lot: z.boolean().optional(),
  conversion_factor: z.number().nullable().optional(),
  conversion_source: z.string().nullable().optional(),
  conversion_confirmed: z.boolean().optional(),
  pack_factor_applied: z.boolean().optional(),
  pack_base_uom: z.string().nullable().optional(),
  gates_ack: z.boolean().optional(),
  lot_ack: z.boolean().optional(),
  label_photo_path: z.string().nullable().optional(),
}).passthrough();

export const DocumentImportReviewRequestPayloadSchema = z.object({
  mode: z.literal('commit'),
  tenant_id: z.string().uuid(),
  business_id: z.string().uuid(),
  doc_kind: z.enum(['ddt', 'fattura']),
  image_mime: z.string().optional(),
  image_b64: z.string().optional(),
  image_storage_path: z.string().optional(),
  review_original_path: z.string().optional(),
  review_original_mime: z.string().optional(),
  review_original_deferred: z.object({
    status: z.enum(['pending', 'attached']),
    deferred_at: z.string(),
    deferred_by_user_id: z.string().uuid(),
    expected_pages: z.number().int().positive().nullable(),
    attached_at: z.string().optional(),
    attached_by_user_id: z.string().uuid().optional(),
    received_pages: z.number().int().positive().optional(),
  }).optional(),
  document_job_id: z.string().uuid().nullable().optional(),
  plan: z.object({
    supplier_id: z.string().uuid().nullable().optional(),
    supplier_create: z.record(z.unknown()).nullable().optional(),
    document: z.object({
      number: z.string(),
      date: z.string(),
      total_amount: z.number().nullable().optional(),
      type: z.string().optional(),
      currency: z.string().optional(),
    }).passthrough(),
    lines: z.array(ReviewPlanLinePayloadSchema),
  }).passthrough(),
  conformity: z.record(z.unknown()).nullable().optional(),
}).passthrough();

export type DocumentImportReviewRequestPayload = z.infer<
  typeof DocumentImportReviewRequestPayloadSchema
>;
